import * as React from 'react';
import {
  Button,
  Content,
  EmptyState,
  EmptyStateBody,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  Title
} from '@patternfly/react-core';
import { Release, currentRelease, releases } from '@app/utils/releaseNotes';
import { ddMMyyyyFormat, fromIsoDate } from '@app/utils/functions';

/**
 * The changes that shipped in one release, generated from the commits between it and the
 * release before it. See `scripts/release.js` and `@app/utils/releaseNotes`.
 */
export const ReleaseNotes: React.FunctionComponent<{ release: Release; showHeading?: boolean }> = ({
  release,
  showHeading = true
}) => (
  <>
    {showHeading && (
      <Title headingLevel="h3" size="md">
        Version {release.version}{' '}
        <span style={{ fontWeight: 'normal', color: 'var(--pf-t--global--text--color--subtle)' }}>
          {/* fromIsoDate, not the raw string: a date-only string parses as UTC midnight and
              renders as the day before west of Greenwich. */}
          — {ddMMyyyyFormat(fromIsoDate(release.date))}
        </span>
      </Title>
    )}
    <List>
      {release.notes.map((note) => (
        <ListItem key={note}>{note}</ListItem>
      ))}
    </List>
  </>
);

/** The notes for the release this build is, with nothing around them. */
export const CurrentReleaseNotes: React.FunctionComponent<{ showHeading?: boolean }> = ({
  showHeading = true
}) => {
  const release = currentRelease();
  if (!release) return null;

  return <ReleaseNotes release={release} showHeading={showHeading} />;
};

export interface WhatsNewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Only ever one release-notes dialog on screen, so a fixed id is safe. */
const TITLE_ID = 'whats-new-modal-title';

/**
 * Every release the application has shipped, newest first — what "What's new" opens onto
 * from About, as opposed to the single latest release the post-login banner shows.
 */
export const WhatsNewModal: React.FunctionComponent<WhatsNewModalProps> = ({ isOpen, onClose }) => (
  // Left to itself PatternFly points aria-labelledby at the modal box, so the dialog is
  // announced as its entire contents. Naming the title element gives it the heading instead.
  <Modal variant={ModalVariant.medium} isOpen={isOpen} onClose={onClose} aria-labelledby={TITLE_ID}>
    <ModalHeader title="Release notes" labelId={TITLE_ID} />
    <ModalBody>
      {releases.length ? (
        <Content>
          {releases.map((release, index) => (
            <section key={release.version} style={{ marginTop: index === 0 ? 0 : '24px' }}>
              <ReleaseNotes release={release} />
            </section>
          ))}
        </Content>
      ) : (
        <EmptyState titleText="No release notes yet" headingLevel="h2">
          <EmptyStateBody>
            Release notes are written when a version is closed with <code>npm run release</code>.
          </EmptyStateBody>
        </EmptyState>
      )}
    </ModalBody>
    <ModalFooter>
      <Button variant="primary" onClick={onClose}>
        Close
      </Button>
    </ModalFooter>
  </Modal>
);
